import { StateCreator } from 'zustand';
import { DocgenClient } from '../../data/docgenClient';
import {
	DownloadFileResponse,
	GetThumbnailResponse,
} from '../../data/interfaces/DocumentTemplateFile';
import { t } from '../../utils';
import { isThumbnailSupported } from '../../utils/access';
import { storeResetFns, useAppStore } from '.';

type State = {
	downloadUrl: string;
	thumbnailUrl: string;
	dataUrl: Blob;
};

type Actions = {
	fetchDownloadUrl: (templateRID?: string) => Promise<void>;
	fetchDataUrl: (downloadUrl: string) => Promise<void>;
	fetchThumbnailUrl: (templateRID: string) => Promise<void>;
	setThumbnailUrl: (url: string) => void;
	resetUrlSliceData: () => void;
};

export type DownloadUrlState = State & Actions;

const initialState = {
	downloadUrl: null,
	thumbnailUrl: null,
	dataUrl: null,
};

export const createDownloadUrlSlice: StateCreator<DownloadUrlState> = (set, get) => {
	storeResetFns.add(() => {
		set(initialState);
	});

	return {
		...initialState,
		fetchDownloadUrl: async (templateRID?: string) => {
			const { fetchDataUrl } = get();

			let rid = templateRID;
			if (!rid) {
				const documentTemplate = useAppStore.getState().documentTemplate;
				rid = documentTemplate.rid;
			}

			try {
				const createTemplateResponse: DownloadFileResponse =
					await DocgenClient.downloadFile(rid);
				const downloadUrl = createTemplateResponse.downloadUrl;
				set({ downloadUrl });
				return await fetchDataUrl(downloadUrl);
			} catch {
				throw new Error(t('errorMessages.fetchPdfFileError'));
			}
		},
		fetchDataUrl: async downloadUrl => {
			try {
				const response = await fetch(downloadUrl, {
					method: 'GET',
				});
				const blob = await response.blob();

				set({
					dataUrl: blob,
				});
			} catch {
				throw new Error(t('errorMessages.fetchPdfFileError'));
			}
		},
		fetchThumbnailUrl: async (templateRID?: string): Promise<void> => {
			if (!templateRID) {
				templateRID = useAppStore.getState().documentTemplate.rid;
			}
			const { account } = useAppStore.getState();
			if (isThumbnailSupported(account)) {
				try {
					const thumbnailResponse: GetThumbnailResponse =
						await DocgenClient.getFileThumbnail(templateRID);

					set({
						thumbnailUrl: thumbnailResponse.uri,
					});
				} catch {
					throw new Error(t('errorMessages.fetchThumbnailUrlError'));
				}
			}
		},
		setThumbnailUrl: url => {
			set({
				thumbnailUrl: url,
			});
		},
		resetUrlSliceData: () => {
			set(initialState);
		},
	};
};
